import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { Card } from '../../ui/primitives/Card';
import { Button } from '../../ui/primitives/Button';
import { Text } from '../../ui/primitives/Text';
import { Stack } from '../../ui/primitives/Stack';
import { Statistic } from '../../ui/primitives/Statistic';
import { DatePicker } from '../../ui/primitives/DatePicker';
import { Input } from '../../ui/primitives/Input';
import { Table, TableColumn } from '../../ui/primitives/Table';
import { useTokens } from '../../core/theme/TokensProvider';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import CheckinHeatmap from './checkins/CheckinHeatmap';
import {
    CheckinReport as CheckinReportModel,
    CheckinCategoryRow,
    CheckinLeaderboardEntry
} from '../../model/members/CheckinReport';
import { MemberCheckinEntry } from '../../model/members/MemberCheckinEntry';

dayjs.extend(utc);
dayjs.extend(timezone);

/** A check-in row carrying a stable id for the virtualised list (see checkinRows). */
type CheckinRow = MemberCheckinEntry & { rowId: string };

const CheckinReport: React.FC = () => {
    const tokens = useTokens();
    const memberService = useMemberService();
    // The club's configured IANA timezone (locale.timezone) drives how check-in times and the
    // query window are interpreted, so the report reads in club-local wall-clock terms. The
    // server buckets the charts/stats in the same zone.
    const clubTimezone = useClientConfig().locale.timezone || 'Europe/London';
    // Stable identity so the memoised column defs (and therefore AntD's row memoisation)
    // survive re-renders — this runs once per visible row on every list render.
    const formatCheckinTime = useCallback(
        (date: Date) => dayjs(date).tz(clubTimezone).format('ddd DD MMM YYYY HH:mm'),
        [clubTimezone]
    );
    // Default range: this month to date (calendar-to-date).
    const [fromDate, setFromDate] = useState<Dayjs>(dayjs().startOf('month'));
    const [toDate, setToDate] = useState<Dayjs>(dayjs());
    const [report, setReport] = useState<CheckinReportModel | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [memberFilter, setMemberFilter] = useState<string>('');

    const fetchReport = useCallback(async (from: Dayjs, to: Dayjs) => {
        setLoading(true);
        try {
            // Interpret the picked calendar dates as club-timezone days, so the query window
            // aligns with the backend's club-timezone day/hour bucketing. Sent as UTC instants.
            const result = await memberService.getClubCheckins(
                dayjs.tz(from.format('YYYY-MM-DD'), clubTimezone).startOf('day').toISOString(),
                dayjs.tz(to.format('YYYY-MM-DD'), clubTimezone).endOf('day').toISOString()
            );
            setReport(result);
        } catch (error) {
            // ClubBookingApiClient surfaces the error notification; keep the screen usable.
            console.error('Error fetching club check-in report:', error);
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [memberService, clubTimezone]);

    useEffect(() => {
        fetchReport(dayjs().startOf('month'), dayjs());
    }, [fetchReport]);

    const applyQuickRange = (from: Dayjs, to: Dayjs) => {
        setFromDate(from);
        setToDate(to);
        fetchReport(from, to);
    };

    const stats = report?.stats;

    // The report counts visits, not scans. Say so — and say how many scans were absorbed —
    // so a total that is smaller than the number of card taps reads as the rule working
    // rather than as missing data. Silent when the club has the window turned off, or when
    // nothing in this period was actually collapsed.
    const duplicateNote = useMemo(() => {
        // An API that predates these fields (see CheckinReportStats) sends neither — say
        // nothing rather than throwing on undefined and taking the whole screen down.
        const minutes = stats?.duplicateWindowMinutes;
        const raw = stats?.rawCheckins;
        if (!stats || minutes == null || raw == null || minutes <= 0) return null;
        const absorbed = raw - stats.totalCheckins;
        const window = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
        if (absorbed <= 0) {
            return `Repeat scans within ${window} count as one check-in. None were found in this period.`;
        }
        return `Repeat scans within ${window} count as one check-in: `
            + `${raw.toLocaleString()} scans counted as `
            + `${stats.totalCheckins.toLocaleString()} check-ins `
            + `(${absorbed.toLocaleString()} repeat ${absorbed === 1 ? 'scan' : 'scans'}).`;
    }, [stats]);

    const returnedCount = report?.checkins.length ?? 0;
    const isTruncated = stats != null && returnedCount < stats.totalCheckins;
    const busiestDayLabel = stats?.busiestDay
        // The server sends a bare club-local calendar date; format with dayjs.utc so the
        // date-only value renders as-is regardless of the browser's own offset.
        ? `${dayjs.utc(stats.busiestDay.date).format('ddd DD MMM')} (${stats.busiestDay.count})`
        : '—';

    // Visit-frequency buckets are already labelled server-side ("1", "2-3", "4-7", "8+").
    const frequencyData = useMemo(
        () => (report?.visitFrequency ?? []).map(b => ({ label: b.label, members: b.memberCount })),
        [report]
    );

    // AntD's virtual body calls rowKey WITHOUT an index, so the key can't lean on one — two
    // check-ins for the same member in the same second would collide, giving duplicate React
    // keys and a shared height-cache entry (rows visibly jump while scrolling). Stamp an
    // intrinsically-unique id onto each row instead, memoised so the list isn't re-flattened.
    const checkinRows: CheckinRow[] = useMemo(
        () => (report?.checkins ?? []).map((c, index) => ({
            ...c,
            // Use the revived Date's own getTime(). `new Date(dateObj)` would stringify and
            // re-parse, dropping milliseconds — precision this key actively needs. The guard
            // is because DateReviver only revives fields it recognises, so the declared `Date`
            // type isn't a runtime guarantee.
            rowId: `${c.membershipId}-${(c.checkInUtc instanceof Date
                ? c.checkInUtc
                : new Date(c.checkInUtc)).getTime()}-${index}`
        })),
        [report]
    );

    // Free-text filter over the loaded rows. Virtualisation means browser find-in-page only
    // sees the visible window, so the screen needs its own way to locate a member in the period.
    const filteredCheckins = useMemo(() => {
        const term = memberFilter.trim().toLowerCase();
        if (!term) return checkinRows;
        return checkinRows.filter(c =>
            c.memberName.toLowerCase().includes(term) || c.membershipId.toLowerCase().includes(term));
    }, [checkinRows, memberFilter]);

    // Widest bar in the category table, for the inline share bar.
    const maxCategoryCheckins = useMemo(
        () => (report?.categories ?? []).reduce((highest, c) => Math.max(highest, c.checkins), 0),
        [report]
    );

    // The deltas compare against the immediately preceding window of the same length — spell that
    // out, since for a month-to-date range it is "the same number of days before", not "last month".
    // The range is inclusive of both ends (hence + 1), mirroring the server's comparison window.
    const comparisonLabel = useMemo(() => {
        const days = Math.max(1, toDate.diff(fromDate, 'day') + 1);
        const previousTo = fromDate.subtract(1, 'day');
        const previousFrom = previousTo.subtract(days - 1, 'day');
        return `${previousFrom.format('DD MMM')} – ${previousTo.format('DD MMM')}`;
    }, [fromDate, toDate]);

    // Trend indicator for a stat tile: null delta means there was no prior period to compare to.
    const deltaSuffix = (delta: number | null | undefined, previous?: number) => {
        if (delta == null) return undefined;
        const rounded = Math.abs(delta);
        const tone = delta > 0 ? tokens.color.success : delta < 0 ? tokens.color.danger : tokens.color.text.secondary;
        const arrow = delta > 0 ? '▲' : delta < 0 ? '▼' : '–';
        return (
            <span
                style={{ fontSize: tokens.fontSize.sm, color: tone }}
                title={`vs ${comparisonLabel}${previous == null ? '' : ` (${previous.toLocaleString()})`}`}
            >
                {arrow} {rounded}%
            </span>
        );
    };

    const categoryColumns: TableColumn<CheckinCategoryRow>[] = useMemo(() => [
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) => a.category.localeCompare(b.category)
        },
        {
            title: 'Check-ins',
            dataIndex: 'checkins',
            key: 'checkins',
            defaultSortOrder: 'descend',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) => a.checkins - b.checkins,
            render: (checkins: number) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
                    <span>{checkins.toLocaleString()}</span>
                    <div
                        aria-hidden
                        style={{
                            height: 6,
                            borderRadius: tokens.radius.sm,
                            backgroundColor: tokens.color.primary,
                            opacity: 0.6,
                            width: maxCategoryCheckins > 0 ? `${(checkins / maxCategoryCheckins) * 60}px` : 0
                        }}
                    />
                </div>
            )
        },
        {
            title: 'Members attended',
            dataIndex: 'uniqueMembers',
            key: 'uniqueMembers',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) => a.uniqueMembers - b.uniqueMembers
        },
        {
            title: 'Active members',
            dataIndex: 'activeMembers',
            key: 'activeMembers',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) => a.activeMembers - b.activeMembers
        },
        {
            title: 'Participation',
            dataIndex: 'participationPercent',
            key: 'participationPercent',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) =>
                (a.participationPercent ?? -1) - (b.participationPercent ?? -1),
            render: (percent: number | null, row: CheckinCategoryRow) =>
                percent == null ? (
                    '—'
                ) : (
                    <span title={`${row.activeAttendees} of ${row.activeMembers} active members checked in`}>
                        {percent}%{' '}
                        <span style={{ color: tokens.color.text.secondary, fontSize: tokens.fontSize.xs }}>
                            ({row.activeAttendees}/{row.activeMembers})
                        </span>
                    </span>
                )
        },
        {
            title: 'Visits / member',
            dataIndex: 'visitsPerMember',
            key: 'visitsPerMember',
            sorter: (a: CheckinCategoryRow, b: CheckinCategoryRow) =>
                (a.visitsPerMember ?? -1) - (b.visitsPerMember ?? -1),
            render: (visits: number | null) => (visits == null ? '—' : visits.toFixed(1))
        }
    ], [tokens, maxCategoryCheckins]);

    const leaderboardColumns: TableColumn<CheckinLeaderboardEntry>[] = useMemo(() => [
        { title: 'Member', dataIndex: 'memberName', key: 'memberName' },
        { title: 'Membership ID', dataIndex: 'membershipId', key: 'membershipId' },
        { title: 'Category', dataIndex: 'membershipCategory', key: 'membershipCategory' },
        { title: 'Check-ins', dataIndex: 'checkins', key: 'checkins' }
    ], []);

    // Memoised: the list can hold thousands of rows, and rebuilding the column array on every
    // render would invalidate AntD's row memoisation and re-render all of them.
    // Widths are required for virtual scrolling (rows are positioned, not measured).
    const columns: TableColumn<CheckinRow>[] = useMemo(() => [
        {
            title: 'Member',
            dataIndex: 'memberName',
            key: 'memberName',
            width: 220
        },
        {
            title: 'Membership ID',
            dataIndex: 'membershipId',
            key: 'membershipId',
            width: 150
        },
        {
            title: 'Membership Category',
            dataIndex: 'membershipCategory',
            key: 'membershipCategory',
            width: 190
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120
        },
        {
            title: 'Check-in Time',
            dataIndex: 'checkInUtc',
            key: 'checkInUtc',
            width: 220,
            render: (date: Date) => formatCheckinTime(date),
            sorter: (a: CheckinRow, b: CheckinRow) =>
                new Date(a.checkInUtc).getTime() - new Date(b.checkInUtc).getTime(),
            defaultSortOrder: 'descend'
        }
    ], [formatCheckinTime]);

    return (
        <Stack gap={5} style={{ padding: tokens.space[6] }} data-testid="checkin-report">
            <Text variant="h2">Club Check-ins</Text>

            {/* Controls */}
            <Card>
                <Stack gap={4}>
                    {/* Quick-select buttons (calendar-to-date) */}
                    <Stack direction="horizontal" gap={2} align="center" wrap>
                        <Text variant="body" color="secondary" weight="medium">Quick Select:</Text>
                        <Button size="sm" data-testid="quick-today" onClick={() => applyQuickRange(dayjs().startOf('day'), dayjs())}>
                            Today
                        </Button>
                        <Button size="sm" data-testid="quick-week" onClick={() => applyQuickRange(dayjs().startOf('week'), dayjs())}>
                            This Week
                        </Button>
                        <Button size="sm" data-testid="quick-month" onClick={() => applyQuickRange(dayjs().startOf('month'), dayjs())}>
                            This Month
                        </Button>
                        <Button size="sm" data-testid="quick-year" onClick={() => applyQuickRange(dayjs().startOf('year'), dayjs())}>
                            This Year
                        </Button>
                    </Stack>

                    {/* Date pickers + actions */}
                    <Stack direction="horizontal" gap={2} align="center" wrap>
                        <DatePicker
                            value={fromDate}
                            onChange={(date) => date && setFromDate(date)}
                            format="DD/MM/YYYY"
                            placeholder="From Date"
                            data-testid="checkin-from-date"
                        />
                        <DatePicker
                            value={toDate}
                            onChange={(date) => date && setToDate(date)}
                            format="DD/MM/YYYY"
                            placeholder="To Date"
                            data-testid="checkin-to-date"
                        />
                        <Button
                            variant="primary"
                            icon={<SearchOutlined />}
                            onClick={() => fetchReport(fromDate, toDate)}
                            loading={loading}
                            data-testid="checkin-search"
                        >
                            Search
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchReport(fromDate, toDate)}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Card>

            {/* Summary statistics */}
            <Card header="Summary">
                <Stack gap={3}>
                <Text variant="caption">Trend compared with {comparisonLabel}.</Text>
                {duplicateNote && (
                    <Text variant="caption" data-testid="checkin-duplicate-window-note">{duplicateNote}</Text>
                )}
                <Stack direction="horizontal" gap={5} wrap>
                    <div style={{ flex: '1 1 160px' }}>
                        <Statistic
                            title="Total Check-ins"
                            value={stats?.totalCheckins ?? 0}
                            suffix={deltaSuffix(
                                stats?.comparison?.totalCheckinsDeltaPercent,
                                stats?.comparison?.previousTotalCheckins
                            )}
                        />
                    </div>
                    <div style={{ flex: '1 1 160px' }}>
                        <Statistic
                            title="Unique Members"
                            value={stats?.uniqueMembers ?? 0}
                            suffix={deltaSuffix(
                                stats?.comparison?.uniqueMembersDeltaPercent,
                                stats?.comparison?.previousUniqueMembers
                            )}
                        />
                    </div>
                    <div style={{ flex: '1 1 160px' }}>
                        <Statistic title="Busiest Day" value={busiestDayLabel} />
                    </div>
                    <div style={{ flex: '1 1 160px' }}>
                        <Statistic title="Avg / Day" value={stats?.averagePerDay ?? 0} precision={2} />
                    </div>
                </Stack>
                </Stack>
            </Card>

            {/* When the club is busy — day-of-week x hour */}
            <Card header="Busiest times">
                <CheckinHeatmap cells={report?.heatmap ?? []} />
            </Card>

            {/* Who is actually active — by category */}
            <Card header="Check-ins by category">
                <Stack gap={3}>
                    <Text variant="body" color="secondary">
                        Participation is the share of each category's active members who checked in at all;
                        visits per member is how often those who came returned.
                    </Text>
                    <Table
                        columns={categoryColumns}
                        dataSource={report?.categories ?? []}
                        loading={loading}
                        pagination={false}
                        rowKey={(record: CheckinCategoryRow) => record.category}
                        emptyText="No categories to report on for the selected period"
                        data-testid="checkin-category-table"
                    />
                </Stack>
            </Card>

            {/* Engagement + recognition */}
            <Stack direction="horizontal" gap={5} wrap>
                <div style={{ flex: '1 1 360px', minWidth: 0 }}>
                    <Card header="How often members visited">
                        <div style={{ height: 260 }} data-testid="checkin-frequency-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={frequencyData} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.color.border.default} />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="members" name="Members" fill={tokens.color.primary} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <Text variant="caption">Members grouped by number of visits in the selected period.</Text>
                    </Card>
                </div>
                <div style={{ flex: '1 1 360px', minWidth: 0 }}>
                    <Card header="Most frequent attendees">
                        <Table
                            columns={leaderboardColumns}
                            dataSource={report?.topMembers ?? []}
                            loading={loading}
                            pagination={false}
                            rowKey={(record: CheckinLeaderboardEntry) => record.membershipId}
                            emptyText="No attendees in the selected period"
                            data-testid="checkin-leaderboard-table"
                        />
                    </Card>
                </div>
            </Stack>

            {/* Check-ins list (scrollable rather than paginated) */}
            <Card header="Check-ins">
                <Stack gap={3}>
                    {duplicateNote && (
                        <Text variant="body" color="secondary">
                            One row per visit, timestamped at the member's first scan of it.
                        </Text>
                    )}
                    {isTruncated && (
                        <Text variant="body" color="secondary">
                            Showing the {returnedCount.toLocaleString()} most recent of {stats!.totalCheckins.toLocaleString()} check-ins.
                            Narrow the date range to see the rest. The stats above cover the full period.
                        </Text>
                    )}
                    <Stack direction="horizontal" gap={2} align="center" wrap>
                        <Input
                            value={memberFilter}
                            onChange={(e) => setMemberFilter(e.target.value)}
                            placeholder="Filter by member name or membership ID"
                            allowClear
                            style={{ maxWidth: 340 }}
                            data-testid="checkin-list-filter"
                        />
                        {memberFilter.trim() && (
                            <Text variant="body" color="secondary">
                                {filteredCheckins.length.toLocaleString()} of {checkinRows.length.toLocaleString()} shown
                            </Text>
                        )}
                    </Stack>
                    <Table
                        columns={columns}
                        dataSource={filteredCheckins}
                        loading={loading}
                        pagination={false}
                        // Virtualised: a month at a busy club is thousands of rows, and rendering
                        // them all is what made this screen lag. Only the visible window is in the
                        // DOM — the list still scrolls through every check-in in the period.
                        virtual
                        scroll={{ y: 480, x: 900 }}
                        rowKey="rowId"
                        emptyText="No check-ins in the selected period"
                        data-testid="checkin-report-table"
                    />
                </Stack>
            </Card>
        </Stack>
    );
};

export default CheckinReport;
